import { ServiceTypes } from 'constants/ServiceTypes';
import { ReactComponent as SettingsIcon } from 'images/TopBar/settingsIcon.svg';
import { ISession } from 'interfaces/session/ISession';
import { ISessionUpdateRequest } from 'interfaces/session/ISessionUpdateRequest';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useInjection } from 'services/ioc/ioc.react';

import Skeleton from '@trackman/web-shared-components/components/LoadingPlaceholder/Skeleton';
import { ReactComponent as CloseIcon } from 'components/icons/closeIcon.svg';
import SessionDropdown from 'components/SessionDropdown/SessionDropdown';
import { IconWrapper } from 'components/UmpireSearch/UmpireSearchStyles';
import { AppPermissions } from 'constants/AppPermissions';
import { NotificationType, SnackbarPosition } from 'interfaces/template/INotification';
import { IUmpire } from 'interfaces/umpire/IUmpire';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { PlaysGridRowManager } from 'services/PlaysGridRowManager';
import { ApplicationUserRights } from 'stores/ApplicationUserRightsStore';
import { AutosaveStore } from 'stores/AutosaveStore';
import { SessionVerificationStore } from 'stores/SessionVerificationStore';
import SnackbarStore from 'stores/SnackbarStore';
import { notify } from '../../../../services/NotificationService';
import { WarmupToggle } from '../WarmupToggle/WarmupToggle';
import AutosaveToggle from './AutosaveToggle/AutosaveToggle';
import { Controls } from './Controls/Controls';
import { SettingsLabelContainer } from './Controls/ControlsStyles';
import EditSessionModal from './EditSessionModal/EditSessionModal';
import { ControlsSkeleton } from './skeletons/ControlsSkeleton';
import { SettingsSkeleton } from './skeletons/SettingsSkeleton';
import { TabsSkeleton } from './skeletons/TabsSkeleton';
import { TeamsSkeleton } from './skeletons/TeamsSkeleton';
import {
  ButtonsWrapper,
  Divider,
  FirstRow,
  SecondRow,
  SendWrapper,
  SettingsBtn,
  StyledButton,
  SubmitControls,
  SubmitWrapper,
  TeamsWrapper,
  TopBarData,
  TopBarWrapper,
  UmpirePlaceholderInfo,
} from './styles/TopBarStyles';
import Tabs from './Tabs/Tabs';
import Teams from './Teams';
import UmpirePlaceholderModal from './UmpirePlaceholderModal/UmpirePlaceholderModal';

interface IProps {
  session: ISession | undefined;
  onSubmitCallback: (updateRequest: ISessionUpdateRequest) => Promise<void>;
  onCsvSelected: (file: any) => void;
  shouldDisplayWarmups: boolean;
  shouldDisplayWarmupsCallback?: (checked: boolean) => void;
  onSessionUpdated?: (oldSession: ISessionUpdateRequest, newSession: ISessionUpdateRequest) => void;
  rowManager: PlaysGridRowManager;
}

export const TopBar: React.FC<IProps> = observer((props) => {
  const { session, onSubmitCallback, onCsvSelected, shouldDisplayWarmups, shouldDisplayWarmupsCallback, onSessionUpdated, rowManager } =
    props;
  const [sessionUpdateRequest, setSessionUpdateRequest] = useState<ISessionUpdateRequest>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isPlayerTrackingVerified, setIsPlayerTrackingVerified] = useState(false);
  const autosaveStore = useInjection<AutosaveStore>(ServiceTypes.AutosaveStore);
  const snackbarStore = useInjection<SnackbarStore>(ServiceTypes.SnackbarStore);
  const sessionVerifierStore = useInjection<SessionVerificationStore>(ServiceTypes.SessionVerificationStore);
  const [contextualDataLoading, setContextualDataLoading] = useState(false);
  const [isUmpireModalVisible, setIsUmpireModalVisible] = useState(false);
  const [selectedUmpire, setSelectedUmpire] = useState<IUmpire>();
  const history = useHistory();

  useEffect(() => {
    setSelectedUmpire(session?.umpire);

    if (session?.umpire?.isPlaceholder) {
      setIsUmpireModalVisible(true);
    }
  }, []);

  useEffect(() => {
    if (session) {
      setIsVerified(session.isVerified);
      setIsPlayerTrackingVerified(session.isPlayerTrackingVerified);
    }
  }, [session?.isVerified, session?.isPlayerTrackingVerified]);

  useEffect(() => {
    if (session) {
      setSessionUpdateRequest({ ...session } as ISessionUpdateRequest);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      snackbarStore.pushNotification(
        `Ball Tracking updated to ${isVerified ? 'verified' : 'not verified'}`,
        NotificationType.success,
        SnackbarPosition.LeftBottom,
        3000
      );
    }
  }, [isVerified]);

  useEffect(() => {
    if (session) {
      snackbarStore.pushNotification(
        `Player Tracking updated to ${isPlayerTrackingVerified ? 'verified' : 'not verified'}`,
        NotificationType.success,
        SnackbarPosition.LeftBottom,
        3000
      );
    }
  }, [isPlayerTrackingVerified]);

  const onSubmit = async () => {
    try {
      if (!sessionUpdateRequest?.venue.id || !sessionUpdateRequest.field.id) {
        notify('Error', 'Field and Venue must be selected', 'error');
        return;
      }
      if (!sessionUpdateRequest?.homeTeam.id || !sessionUpdateRequest.awayTeam.id) {
        notify('Error', 'Both teams must be selected', 'error');
        return;
      }
      setIsLoading(true);
      await onSubmitCallback({ ...sessionUpdateRequest, isVerified, isPlayerTrackingVerified });
      autosaveStore.clear();
    } finally {
      setIsLoading(false);
    }
  };

  const onMetadataChange = (update: ISessionUpdateRequest) => {
    if (!sessionUpdateRequest) {
      return;
    }
    const oldSession = { ...sessionUpdateRequest };
    const newSession = { ...update };
    setSessionUpdateRequest(newSession);
    if (onSessionUpdated) {
      onSessionUpdated(oldSession, newSession);
    }
  };

  const openSettingsModalHandler = async () => {
    setContextualDataLoading(true);

    if (ApplicationUserRights.IsAllowed(AppPermissions.PortalAccess)) {
      setIsSettingsModalVisible(true);

      await sessionVerifierStore.getContextualData().then(() => {
        setContextualDataLoading(false);
      });
    }
  };

  const exitSessionHandler = () => {
    if (session) {
      const date = moment.parseZone(session.localStartTime).format('YYYY-MM-DD');

      history.push(`/search/teamId/undefined/date/${date}`);
    }
  };

  return (
    <TopBarWrapper>
      <FirstRow>
        <TopBarData>
          {!session ? (
            <Skeleton variant='rectangular' width={'32px'} height={'32px'} />
          ) : (
            <StyledButton
              label=''
              startIcon={<CloseIcon />}
              intent='primary'
              onClick={() => exitSessionHandler()}
              width={'32px'}
              style={{ minWidth: '32px' }}
            />
          )}
          {!session ? (
            <TeamsSkeleton />
          ) : (
            <TeamsWrapper>
              <Teams
                awayTeam={session.awayTeam}
                homeTeam={session.homeTeam}
                fieldName={sessionUpdateRequest?.field.name || ''}
                gameReference={session.gameReference}
                localStartTime={session.localStartTime}
              />
            </TeamsWrapper>
          )}
        </TopBarData>
        {!session ? (
          <ControlsSkeleton />
        ) : (
          <ButtonsWrapper>
            {shouldDisplayWarmupsCallback && (
              <WarmupToggle
                shouldDisplayWarmups={shouldDisplayWarmups}
                shouldDisplayWarmupsCallback={shouldDisplayWarmupsCallback}
                disabled={false}
              />
            )}
            <SettingsBtn data-testid='settings-button' onClick={openSettingsModalHandler}>
              {selectedUmpire?.isPlaceholder && (
                <SettingsLabelContainer>
                  <UmpirePlaceholderInfo>Replace placeholder umpire</UmpirePlaceholderInfo>
                </SettingsLabelContainer>
              )}
              <IconWrapper>
                <SettingsIcon />
              </IconWrapper>
            </SettingsBtn>
            <SubmitWrapper>
              <Divider />
              <SubmitControls>
                <AutosaveToggle />
                <SendWrapper>
                  <SessionDropdown
                    isVerified={isVerified}
                    setIsVerified={setIsVerified}
                    isPlayerTrackingVerified={isPlayerTrackingVerified}
                    setIsPlayerTrackingVerified={setIsPlayerTrackingVerified}
                    onSubmit={onSubmit}
                    isPlayerTrackingEnabled={session.isPlayerTrackingEnabled}
                  />
                </SendWrapper>
              </SubmitControls>
            </SubmitWrapper>
          </ButtonsWrapper>
        )}
      </FirstRow>
      <SecondRow>
        {!session ? (
          <TabsSkeleton />
        ) : (
          <Tabs
            isPlayerTrackingEnabled={session.isPlayerTrackingEnabled}
            isVerified={isVerified}
            isPlayerTrackingVerified={isPlayerTrackingVerified}
          />
        )}
        {!session ? <SettingsSkeleton /> : <Controls />}
      </SecondRow>
      <EditSessionModal
        visible={isSettingsModalVisible}
        session={session}
        onDismissCallback={() => setIsSettingsModalVisible(false)}
        onApplyCallback={(update: ISessionUpdateRequest) => {
          onMetadataChange(update);
          setIsSettingsModalVisible(false);
        }}
        onCsvSelected={(file: any) => {
          if (onCsvSelected) {
            onCsvSelected(file);
            setIsSettingsModalVisible(false);
          }
        }}
        contextualDataLoading={contextualDataLoading}
        selectedUmpire={selectedUmpire}
        setSelectedUmpire={setSelectedUmpire}
      />
      <UmpirePlaceholderModal
        onConfirmCallback={() => {
          setIsUmpireModalVisible(false);
          openSettingsModalHandler();
        }}
        onDismissCallback={() => {
          setIsUmpireModalVisible(false);
        }}
        open={isUmpireModalVisible}
      />
    </TopBarWrapper>
  );
});
